import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { API_CONFIG } from '../config';
import { HtmlContent } from '../types';

interface Language {
  _id: string;
  name: string;
  code: string;
  nativeName: string;
  isActive: boolean;
  isRTL?: boolean;
}

// Helper function to get display title (handles both string and multilingual formats)
const getDisplayTitle = (title: string | Record<string, string> | undefined): string => {
  if (!title) return '';
  if (typeof title === 'string') return title;
  // Multilingual format - prefer EN, fallback to first available
  return title.EN || title.en || Object.values(title)[0] || '';
};

// Helper function to get display content
const getDisplayContent = (content: string | Record<string, string> | undefined): string => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  // Multilingual format - prefer EN, fallback to first available
  return content.EN || content.en || Object.values(content)[0] || '';
};

const TermsConditions: React.FC = () => {
  const navigate = useNavigate();
  const [termsConditions, setTermsConditions] = useState<HtmlContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRTL, setIsRTL] = useState(false);

  const fetchTermsConditions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // get query param lang from url
      const lang = new URLSearchParams(window.location.search).get('lang') ?? 'EN';
      
      // Fetch language info to check RTL status (using public endpoint)
      try {
        // Use public endpoint that doesn't require authentication
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/devices/languages`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const languages = data.data as Language[];
            const currentLang = languages.find(l => l.code.toUpperCase() === lang.toUpperCase());
            setIsRTL(currentLang?.isRTL ?? false);
          }
        }
      } catch (e) {
        console.error('Error fetching language info:', e);
        // Continue without RTL info if languages can't be fetched
      }
      
      const response = await apiService.getPublicTermsConditions(lang);
      
      if (response.success && response.data) {
        setTermsConditions(response.data);
      } else {
        setError(response.error || 'Failed to load terms and conditions');
      }
    } catch (err: any) {
      console.error('Error fetching terms and conditions:', err);
      setError(err.response?.data?.error || 'Failed to load terms and conditions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTermsConditions();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate('/admin/content');
  };

  const handleRefresh = () => {
    fetchTermsConditions();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Terms & Conditions...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        p={3}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        flexGrow: 1, 
        backgroundColor: '#f5f5f5', 
        minHeight: '100vh',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      <AppBar position="static" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton
            edge={isRTL ? 'end' : 'start'}
            color="inherit"
            onClick={handleBack}
            sx={{ color: 'black', [isRTL ? 'ml' : 'mr']: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'black', textAlign: isRTL ? 'right' : 'left' }}>
            Terms & Conditions
          </Typography>
          {/* <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ mr: 1 }}
          >
            Edit
          </Button> */}
          <IconButton onClick={handleRefresh} sx={{ color: 'black' }}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 4 }}>
          {termsConditions ? (
            <>
              <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, color: '#1976d2' }}>
                {getDisplayTitle(termsConditions.title)}
              </Typography>
              
              <Box
                component="div"
                sx={{
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    color: '#333',
                    marginTop: 3,
                    marginBottom: 2,
                  },
                  '& p': {
                    marginBottom: 2,
                    lineHeight: 1.6,
                    color: '#555',
                  },
                  '& ul, & ol': {
                    marginBottom: 2,
                    [isRTL ? 'paddingRight' : 'paddingLeft']: 3,
                  },
                  '& li': {
                    marginBottom: 1,
                    lineHeight: 1.6,
                    color: '#555',
                  },
                  '& strong': {
                    color: '#333',
                    fontWeight: 600,
                  },
                  '& a': {
                    color: '#1976d2',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                }}
                dangerouslySetInnerHTML={{ __html: getDisplayContent(termsConditions.content) }}
              />
              
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="text.secondary">
                  Last updated: {new Date(termsConditions.updatedAt).toLocaleDateString()}
                </Typography>
              </Box>
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                No terms and conditions content found
              </Typography>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ mt: 2 }}
              >
                Create Terms & Conditions
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsConditions;
